import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import caseActivitySettingsService from "../services/case-activity-settings-service";
import type { CaseActivitySetting } from "../types/case-activity-settings";

/**
 * Query key for case activity settings
 */
export const caseActivitySettingsKeys = {
	all: ["case-activity-settings"] as const,
	current: () => [...caseActivitySettingsKeys.all, "current"] as const,
	detail: (id: string) => [...caseActivitySettingsKeys.all, id] as const,
};

/**
 * Query options for getting current case activity settings
 */
export const caseActivitySettingsQueryOptions = () =>
	queryOptions({
		queryKey: caseActivitySettingsKeys.current(),
		queryFn: () => caseActivitySettingsService.getCurrent(),
	});

/**
 * Mutation for updating case activity settings
 */
export const useUpdateCaseActivitySettings = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CaseActivitySetting> }) => caseActivitySettingsService.updateById(id, data as CaseActivitySetting),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: caseActivitySettingsKeys.all });
			toast.success("Settings updated successfully");
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || "Failed to update settings");
		},
	});
};

/**
 * Mutation for toggling edit filed_at permission
 */
export const useToggleEditFiledAt = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => caseActivitySettingsService.toggleEditFiledAt(),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: caseActivitySettingsKeys.all });
			toast.success(data.message);
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || "Failed to toggle setting");
		},
	});
};
